import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import type { SelfRatedSupply } from "./self-rated-supply";

const API_URL = import.meta.env.VITE_API_URL;
export interface LabelProviderSetup {
  provider: {
    id: string;
    name: string;
    kind: string;
    active: boolean;
    account: { type: string; configured: boolean };
  } | null;
  supply:
    | (SelfRatedSupply & { apiProviderId: string; credentialId: null })
    | null;
}
export interface SaveLabelProviderSetup {
  apiProviderId: string;
  credentialId: null;
  expectedUpdatedAt: string | null;
  expectedTermsId: string | null;
  active: boolean;
  priority: number;
  serviceIds: string[];
  terms: {
    payeeType: "API_PROVIDER";
    payeeId: string;
    payerType: "EZX";
    settlementTrigger: NonNullable<
      SelfRatedSupply["terms"]
    >["settlementTrigger"];
    currency: string;
  } | null;
}
export type ChitChatsSetup = LabelProviderSetup;
export type SaveChitChatsSetup = SaveLabelProviderSetup;

const key = (carrierId: string) => ["chitchats-label-setup", carrierId];
export function useChitChatsSetup(carrierId: string) {
  const { token } = useSession();
  return useQuery({
    queryKey: key(carrierId),
    enabled: !!token,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      (
        await axios.get<{ data: ChitChatsSetup }>(
          `${API_URL}/api/v2/carriers/${carrierId}/chitchats-supply`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ).data.data,
  });
}
export function useSaveChitChatsSetup(carrierId: string) {
  const { token } = useSession();
  const queries = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveChitChatsSetup) =>
      (
        await axios.put<{ data: ChitChatsSetup }>(
          `${API_URL}/api/v2/carriers/${carrierId}/chitchats-supply`,
          input,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ).data.data,
    onSuccess: async (data) => {
      queries.setQueryData(key(carrierId), data);
      await queries.invalidateQueries({ queryKey: ["carriers"] });
      await queries.invalidateQueries({ queryKey: ["carrier-services"] });
    },
  });
}
